import { IModel, Collection } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Transformation } from 'app/model/dataflow/transformation';

import {
    IParameterizable,
    OwnedSet,
    ActionConstants,
    ValidationConstants,
    Shadow,
    IViolation,
    ModelConstants,
    IValidationContext
} from 'shell-models-lib';
import { InputTransformation } from './inputtransformation';
import { OutputTransformation } from './outputtransformation';
import { DataFlow } from './dataflow';
import { FlowletParser } from '../adapters/dataflow/flowlet.parser';
import { sanitizeAndFormatString } from 'app/shared/utils/string.util';

@Metamodel.model({
    name: ModelConstants.flowlet,
    display: [stringResources.Flowlet, stringResources.Flowlets],
    reusable: true,
    referable: true,
    constraints: {
        name: {
            required: true,
            pattern: ValidationConstants.factoryEntityNameValidator,
            uniqueModelName: true
        }
    },
    validator: Flowlet.prototype.validate,
    tags: { gitValidator: Flowlet.prototype.gitValidate },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.dataFlowsWrite
        },
        deleteResource: {
            action: ActionConstants.dataFlowsDelete
        }
    }
})
export class Flowlet extends DataFlow implements IParameterizable {

    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        type: InputTransformation.MODEL,
        collection: Collection.Set
    })
    public inputs = OwnedSet.create<InputTransformation>(this);

    @Metamodel.attribute({
        type: OutputTransformation.MODEL,
        collection: Collection.Set
    })
    public outputs = OwnedSet.create<OutputTransformation>(this);

    public showDataPreview: boolean = false;

    public addInput(input: InputTransformation): Flowlet {
        input.order = this.inputs.size;
        this.inputs.add(input);
        return this;
    }

    public addOutput(output: OutputTransformation): Flowlet {
        this.outputs.add(output);
        return this;
    }

    public getAllTransformations(): Transformation[] {
        return [...this.sources, ...this.sourceFlowlets, ...this.inputs, ...this.transformations, ...this.sinks, ...this.outputs];
    }

    public getSortedInputs(): InputTransformation[] {
        return [...this.inputs].sort((a, b) => a.order - b.order);
    }

    public addTransformation(transformation: Transformation): Flowlet {
        switch (Metamodel.modelOf(transformation).name) {
            case Metamodel.modelFor(InputTransformation).name:
                return this.addInput(transformation as InputTransformation);
            case Metamodel.modelFor(OutputTransformation).name:
                return this.addOutput(transformation as OutputTransformation);
            default:
                return super.addTransformation(transformation) as Flowlet;
        }
    }

    public removeTransformation(transformation: Transformation): void {
        switch (Metamodel.modelOf(transformation).name) {
            case Metamodel.modelFor(InputTransformation).name:
                transformation.outputStreams.forEach((outputStream) => {
                    for (let i = outputStream.consumers.length - 1; i >= 0; i--) {
                        const consumer = outputStream.consumers[i];
                        consumer.detachInputStream(outputStream);
                    }
                });
                transformation.removeAllOutputStreams();
                const order = (transformation as InputTransformation).order;
                this.inputs.delete(transformation as InputTransformation);
                this.inputs.forEach((transformation) => {
                    if (transformation.order > order) {
                        transformation.order--;
                    }
                });
                break;
            case Metamodel.modelFor(OutputTransformation).name:
                transformation.inputStreams.forEach((inputStream) => {
                    transformation.detachInputStream(inputStream);
                });
                transformation.removeAllOutputStreams();
                this.outputs.delete(transformation as OutputTransformation);
                break;
            default:
                return super.removeTransformation(transformation);
        }
        this.forceNewDebugJobVersion();
    }

    public async validate(owner: unknown, context: IValidationContext): Promise<IViolation[] | undefined> {
        const violations: IViolation[] = [];

        if (!this.cacheUpdated) {
            await this.setupCache(context.contextData);
        }

        if (!this.hasSources()) {
            violations.push({
                context: this,
                message: () => sanitizeAndFormatString(stringResources.FlowletErrorNoSource, this.name)
            });
        }
        if (!this.hasSinks()) {
            violations.push({
                context: this,
                message: () => sanitizeAndFormatString(stringResources.FlowletErrorNoSink, this.name)
            });
        }

        violations.push(...this.validateUniqueNames(), ...this.validateInputs());
        return violations;
    }

    public async tearDown(context: { inlineDataset?: boolean }): Promise<void> {
        super.tearDownTransformations(context);
        this.inputs.clear();
        this.outputs.clear();

        Shadow.cast(context, this);
    }

    public inputsHaveDebugData(): boolean {
        return [...this.inputs].every((input) => !!input.debugData);
    }

    protected addInputStreams(): void {
        this.outputs.forEach((output) => this._addInputStream(output));
        super.addInputStreams();
    }

    protected getParser(): FlowletParser {
        return new FlowletParser();
    }

    protected startStopTransformation(transformationModel: Transformation): boolean {
        return super.startStopTransformation(transformationModel) && !(transformationModel instanceof InputTransformation);
    }

    protected hasSources(): boolean {
        return this.sources.size !== 0 || this.inputs.size !== 0 || this.sourceFlowlets.size !== 0;
    }

    protected hasSinks(): boolean {
        return this.sinks.size !== 0 || this.outputs.size !== 0;
    }

    protected allSources(): Transformation[] {
        return [...this.sources, ...this.inputs, ...this.sourceFlowlets];
    }
}
